import { useRef } from 'react'
import type { ReactNode, RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { Printer } from 'lucide-react'
import { PAPER_SIZES, mmToPx } from '@quebon/shared-learning'
import { useLmsPrint } from '@/hooks/useLmsPrint'
import { LmsPdfDownloadButton } from '@/components/admin/print/LmsPdfDownloadButton'
import { AiqBellCurve } from '@/components/admin/diagnostics/AiqBellCurve'
import type { AiqDiagnosticReportResponse, AiqDomainScore } from '@/api/aiq'
import type { AiqDomain, MathBand } from '@/constants/aiq'
import { DOMAIN_DEFINITION, DOMAIN_ORDER, DOMAIN_SLOGAN, TYPE_MAP, bandOf, scoreDiffText } from '@/constants/aiq'

export const AIQ_BAND_COLORS: Record<MathBand, string> = {
  high: '#4599ff',
  mid: '#ffaa00',
  low: '#ff675e',
}

export const AIQ_REPORT_PAGE_STYLE = {
  width: mmToPx(PAPER_SIZES.A4.width),
  height: mmToPx(PAPER_SIZES.A4.height),
} as const

const AIQ_PRINT_PAGE_STYLE = `
  @page { size: A4 portrait; margin: 0; }
  @media print {
    html, body { margin: 0 !important; padding: 0 !important; }
    .print-action-bar { display: none !important; }
    #aiq-report-root, #aiq-report-root * { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .diagnostic-report-page { width: ${AIQ_REPORT_PAGE_STYLE.width}px !important; height: ${AIQ_REPORT_PAGE_STYLE.height}px !important; box-sizing: border-box; overflow: hidden; break-after: page; page-break-after: always; }
    .diagnostic-report-page:last-child { break-after: auto; page-break-after: auto; }
  }
`

/** Mount only for standalone reports; embedded report pages use their host's print/PDF actions. */
export function AiqReportPrintActions({ contentRef }: { contentRef: RefObject<HTMLDivElement> }) {
  const { t } = useTranslation('admin')
  const { print, downloadPdf, canDownloadPdf, isDownloading, error } = useLmsPrint({
    contentRef,
    pageStyle: AIQ_PRINT_PAGE_STYLE,
  })
  return (
    <div className="print-action-bar print:hidden flex justify-end gap-2 border-b border-gray-100 bg-white px-6 py-3">
      <button
        type="button"
        onClick={() => print()}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
      >
        <Printer className="h-4 w-4" /> <span>{t('studentAiqReport.print')}</span>
      </button>
      {canDownloadPdf && (
        <LmsPdfDownloadButton onClick={downloadPdf} isDownloading={isDownloading} error={error} />
      )}
    </div>
  )
}

const AIQ_DOMAIN_ICONS: Record<AiqDomain, string> = {
  abstraction: '/report/diagnostic/icon-target.svg',
  change: '/report/diagnostic/icon-graph.svg',
  relation: '/report/diagnostic/icon-relation.svg',
}

const CUMULATIVE_INFO_COLORS: Record<AiqDomain, string> = {
  abstraction: '#4599ff',
  change: '#23b8bc',
  relation: '#6765e0',
}

const CUMULATIVE_INFO_ICONS: Record<AiqDomain, string> = {
  abstraction: '/report/cumulative/icon-target.svg',
  change: '/report/cumulative/icon-graph.svg',
  relation: '/report/cumulative/icon-relation.svg',
}

const DOMAIN_SERIES = {
  self: { bar: '#4599FF', text: '#4599FF' },
  sameAge: { bar: '#C3D8F2', text: '#A5C3E9' },
  quebon: { bar: '#A0BFE4', text: '#6F9BCF' },
}

/**
 * Wording a host report substitutes for the diagnostic defaults. The cumulative report reuses these pages with
 * its own header title and date, compares against its World's general learners instead of the same test, and
 * prints one band-fixed narrative per competency (cumulative-report-plan F6, D16).
 */
export interface AiqReportCopy {
  title?: string
  headerDate?: string
  summaryIndexPrefix?: string
  summaryIndexSuffix?: string
  summaryRankPrefix?: string
  distDesc?: string
  distCaption?: string
  infoTitle?: string
  infoHeadline?: string
  infoBody1?: string
  infoBody2?: string
  infoBody3?: string
  infoAppearance?: 'cumulative'
  infoCards?: Partial<Record<AiqDomain, { label: string; headline: string; body: string; emphasis: string }>>
  compareDesc?: string
  comparisonFootnote?: string
  sameGroupLabel?: string
  topGroupLabel?: string
  sameAveragePrefix?: string
  sameAverageValue?: (value: number) => string
  domainHeadlines?: Partial<Record<AiqDomain, string>>
  hideItemCount?: boolean
}

const MINI_COHORT_BAR_MAX_HEIGHT = 88
const DOMAIN_BAR_MAX_HEIGHT = 120

/**
 * 누적 리포트가 쓰는 2쪽 구성(요약 + 상세). 진단 리포트의 수학머리 쪽은 DiagnosticAiqReportPages가 그린다.
 */
export function StudentAiqReportContent({
  data,
  showPrintButton = true,
  className = 'mx-auto max-w-[794px] bg-white shadow-lg print:shadow-none',
  itemCount,
  elapsedMin,
  pageStart = 3,
  gradeLabel,
  copy,
}: {
  data: AiqDiagnosticReportResponse
  showPrintButton?: boolean
  className?: string
  itemCount?: number | null
  elapsedMin?: number | null
  pageStart?: number
  gradeLabel?: string
  copy?: AiqReportCopy
}) {
  const printRef = useRef<HTMLDivElement>(null)

  return (
    <div
      ref={printRef}
      className={className}
      id="aiq-report-root"
      style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
    >
      {showPrintButton && <AiqReportPrintActions contentRef={printRef} />}
      <AiqSummaryPage
        data={data}
        gradeLabel={gradeLabel}
        itemCount={itemCount}
        elapsedMin={elapsedMin}
        pageNumber={pageStart}
        copy={copy}
      />
      <AiqDetailPage data={data} gradeLabel={gradeLabel} pageNumber={pageStart + 1} copy={copy} />
    </div>
  )
}

export function AiqReportHeader({
  data,
  gradeLabel,
  copy,
  subtitle,
}: {
  data: AiqDiagnosticReportResponse
  gradeLabel?: string
  copy?: AiqReportCopy
  subtitle?: string
}) {
  const { t } = useTranslation('admin')
  return (
    // Figma header is 56px with the bottom border inside it: 24px row + 31px + 1px border.
    <header className="flex items-center justify-between border-b border-[#d0d0d0] pb-[31px]">
      <div className="flex shrink-0 items-center gap-3 whitespace-nowrap px-2">
        <img src="/report/diagnostic/logo_kor.svg" alt="" aria-hidden="true" className="h-6 w-[119px]" />
        <span className="h-3.5 w-px bg-[#d0d0d0]" aria-hidden="true" />
        <h1 className="text-base font-bold leading-[19px] text-[#131313]">{copy?.title ?? t('studentAiqReport.title')}</h1>
        {subtitle ? (
          <span data-testid="aiq-header-subtitle" className="-ml-1 text-xs font-medium leading-[18px] text-[#888]">{subtitle}</span>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-4 whitespace-nowrap px-2 text-sm text-[#333]">
        <div className="flex items-center gap-2">
          <span className="flex size-6 items-center justify-center rounded-full bg-[#eee]">
            <img
              data-testid="aiq-header-user-icon"
              src="/report/diagnostic/icon-user.svg"
              alt=""
              className="size-3.5"
              aria-hidden="true"
            />
          </span>
          <span className="flex items-center gap-1">
            <span className="font-medium leading-[21px]">{data.nickName}</span>
            <span className="text-xs leading-[18px] text-[#4599ff]">({gradeLabel ?? data.gradeLabel})</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex size-6 items-center justify-center rounded-full bg-[#eee]">
            <img
              data-testid="aiq-header-calendar-icon"
              src="/report/diagnostic/icon-calendar.svg"
              alt=""
              className="size-3.5"
              aria-hidden="true"
            />
          </span>
          <span className="font-medium leading-[21px]">{copy?.headerDate ?? formatAiqDate(data.diagnosedAt)}</span>
        </div>
      </div>
    </header>
  )
}

function AiqSummaryPage({
  data,
  gradeLabel,
  itemCount,
  elapsedMin,
  pageNumber,
  copy,
}: {
  data: AiqDiagnosticReportResponse
  gradeLabel?: string
  itemCount?: number | null
  elapsedMin?: number | null
  pageNumber: number
  copy?: AiqReportCopy
}) {
  const { t } = useTranslation('admin')
  const band = bandOf(data.aiqScore)
  const activeColor = AIQ_BAND_COLORS[band]

  return (
    <section
      data-testid="aiq-report-summary-page"
      className="diagnostic-report-page relative flex break-before-page flex-col gap-7 bg-white px-12 pb-12 pt-10"
      style={AIQ_REPORT_PAGE_STYLE}
    >
      <AiqReportHeader data={data} gradeLabel={gradeLabel} copy={copy} />

      <section className="flex w-full flex-col gap-6">
        <div className="flex items-center justify-between pb-2 pr-14">
          <div className="flex flex-col gap-4 px-2">
            <div
              data-testid="aiq-summary-headline"
              className="text-xl font-bold leading-[1.7] tracking-[-0.6px] text-[#131313]"
            >
              {copy?.summaryIndexPrefix ? (
                <p className="text-lg">
                  {copy.summaryIndexPrefix}
                  <span data-testid="aiq-summary-score" style={{ color: activeColor }}>{data.aiqScore}</span>
                  {copy.summaryIndexSuffix}
                </p>
              ) : <p>{t('studentAiqReport.summary.studentLine', { nickName: data.nickName })}</p>}
              <p>
                {copy?.summaryIndexPrefix ? copy.summaryRankPrefix : (
                  <>
                    {t('studentAiqReport.summary.aiqPrefix')}
                    <span data-testid="aiq-summary-score" style={{ color: activeColor }}>{data.aiqScore}</span>
                    {t('studentAiqReport.summary.rankPrefix')}
                  </>
                )}
                <span data-testid="aiq-summary-rank" style={{ color: activeColor }}>{data.aiqRank}%</span>
                {t('studentAiqReport.summary.levelPrefix')}
                <span data-testid="aiq-summary-level" style={{ color: activeColor }}>
                  {t(`studentAiqReport.summary.level.${band}`)}
                </span>
                {t('studentAiqReport.summary.suffix')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {copy?.hideItemCount ? null : (
                <MetaPill>
                  {itemCount == null ? t('studentAiqReport.summary.itemCountEmpty') : t('studentAiqReport.summary.itemCount', { count: itemCount })}
                </MetaPill>
              )}
              <MetaPill>
                <span className="text-xs font-semibold text-[#888]">{t('studentAiqReport.summary.elapsed')}</span>
                <span>{t('studentAiqReport.summary.elapsedMin', { value: elapsedMin ?? '—' })}</span>
              </MetaPill>
            </div>
          </div>
          <AiqBandIndicator nickName={data.nickName} band={band} activeColor={activeColor} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <section
            data-testid="aiq-distribution-card"
            className="flex h-[294px] flex-col gap-6 rounded-lg bg-[#f5f5f5] px-6 py-5"
          >
            <div className="flex flex-col gap-2 tracking-[-0.6px] text-[#131313]">
              <h2 className="text-sm font-bold leading-[1.5]">{t('studentAiqReport.distTitle')}</h2>
              <p className="text-xs font-normal leading-[1.5]">{copy?.distDesc ?? t('studentAiqReport.distDesc')}</p>
              <p className="text-xs font-medium leading-[14px] text-[#888]">{copy?.distCaption ?? t('studentAiqReport.distCaption')}</p>
            </div>
            <MiniBellCurve score={data.aiqScore} rank={data.aiqRank} />
          </section>

          <section
            data-testid="aiq-comparison-card"
            className="flex h-[294px] flex-col justify-between rounded-lg bg-[#f5f5f5] px-6 py-5"
          >
            <div className="flex flex-col gap-2 tracking-[-0.6px] text-[#131313]">
              <h2 className="text-sm font-bold leading-[1.5]">{t('studentAiqReport.compareTitle')}</h2>
              <p className="text-xs font-normal leading-[1.5]">{copy?.compareDesc ?? t('studentAiqReport.compareDesc')}</p>
            </div>
            <MiniCohortBars
              nickName={data.nickName}
              aiqScore={data.aiqScore}
              sameAgeAiqScore={data.sameAgeAiqScore}
              quebonAiqScore={data.quebonAiqScore}
              sameGroupLabel={copy?.sameGroupLabel}
              topGroupLabel={copy?.topGroupLabel}
            />
            {copy?.comparisonFootnote ? <ComparisonFootnote>{copy.comparisonFootnote}</ComparisonFootnote> : null}
          </section>
        </div>
      </section>

      <section data-testid="aiq-info-section" className="flex flex-col gap-4">
        <h2 className="px-2 text-base font-bold leading-[1.2] tracking-[-0.6px] text-[#131313]">{copy?.infoTitle ?? t('studentAiqReport.aiqInfo.title')}</h2>
        <div className={`flex flex-col gap-6 rounded-lg bg-[#f5f5f5] ${copy?.infoAppearance === 'cumulative' ? 'p-4' : 'px-6 py-5'}`}>
          <div className={`flex flex-col gap-2 tracking-[-0.6px] text-[#131313] ${copy?.infoAppearance === 'cumulative' ? 'px-2' : ''}`}>
            <p className={`whitespace-pre-line text-sm font-bold ${copy?.infoAppearance === 'cumulative' ? 'leading-[1.7]' : 'leading-[1.5]'}`}>{copy?.infoHeadline ?? t('studentAiqReport.aiqInfo.headline')}</p>
            <div className="text-xs font-normal leading-[1.5]">
              <p>{copy?.infoBody1 ?? t('studentAiqReport.aiqInfo.body1')}</p>
              <p>{copy?.infoBody2 ?? t('studentAiqReport.aiqInfo.body2')}</p>
              <p>{copy?.infoBody3 ?? t('studentAiqReport.aiqInfo.body3')}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <AiqInfoCard domain="abstraction" copy={copy} />
            <AiqInfoCard domain="change" copy={copy} />
            <AiqInfoCard domain="relation" copy={copy} />
          </div>
        </div>
      </section>

      <PageNumber value={pageNumber} />
    </section>
  )
}

function AiqDetailPage({
  data,
  gradeLabel,
  pageNumber,
  copy,
}: {
  data: AiqDiagnosticReportResponse
  gradeLabel?: string
  pageNumber: number
  copy?: AiqReportCopy
}) {
  const { t } = useTranslation('admin')

  return (
    <section
      data-testid="aiq-report-detail-page"
      className="diagnostic-report-page relative flex break-before-page flex-col gap-7 bg-white px-12 pb-12 pt-10"
      style={AIQ_REPORT_PAGE_STYLE}
    >
      <AiqReportHeader data={data} gradeLabel={gradeLabel} copy={copy} />
      <DomainComparisonChart domainScores={data.domainScores} nickName={data.nickName} copy={copy} />
      <section className="flex flex-col gap-4">
        <h2
          data-testid="aiq-detail-title"
          className="px-2 text-base font-bold leading-[1.2] tracking-[-0.6px] text-[#131313]"
        >
          {t('studentAiqReport.detailTitle')}
        </h2>
        <div className="flex flex-col gap-4">
          {DOMAIN_ORDER.map((domain) => {
            const score = data.domainScores.find((item) => item.domain === domain)
            return score ? <DomainDetailCard key={domain} score={score} copy={copy} /> : null
          })}
        </div>
      </section>
      <PageNumber value={pageNumber} />
    </section>
  )
}

export function MetaPill({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-center gap-2 rounded-full bg-[#eee] px-2.5 py-1 text-sm font-medium text-[#333]">
      {children}
    </div>
  )
}

export function AiqBandIndicator({
  nickName,
  band,
  activeColor,
}: {
  nickName: string
  band: MathBand
  activeColor: string
}) {
  const { t } = useTranslation('admin')
  return (
    <div className="relative h-[106px] w-[174px] shrink-0">
      <div className="absolute left-[50px] top-0 flex w-[124px] flex-col gap-1">
        {(['high', 'mid', 'low'] as MathBand[]).map((item) => (
          <div
            key={item}
            data-testid={item === band ? 'student-aiq-band' : undefined}
            className="flex h-8 items-center justify-center rounded-lg text-sm font-bold text-white"
            style={{ backgroundColor: item === band ? activeColor : '#d0d0d0' }}
          >
            {t(`studentAiqReport.band.${item}`)}
          </div>
        ))}
      </div>
      <div
        className="absolute right-[126px] flex h-[18px] items-center gap-3 whitespace-nowrap text-xs font-medium leading-[1.5] tracking-[-0.6px] text-[#888]"
        style={{ top: bandTop(band) }}
      >
        <span>{nickName}</span>
        {/* Figma picker (6×8, pointing at the band), 2px left of the chips. */}
        <span data-testid="student-aiq-band-pointer" className="flex h-2 w-1.5 items-center justify-center" aria-hidden="true">
          <img src="/report/diagnostic/picker.svg" alt="" className="h-[5.6px] w-[7.13px] max-w-none rotate-90" />
        </span>
      </div>
    </div>
  )
}

function AiqInfoCard({ domain, copy }: { domain: AiqDomain; copy?: AiqReportCopy }) {
  const { t } = useTranslation('admin')
  const card = copy?.infoCards?.[domain]
  if (copy?.infoAppearance === 'cumulative' && card) {
    const color = CUMULATIVE_INFO_COLORS[domain]
    return (
      <div className="flex min-w-0 flex-col items-center gap-4 rounded-lg bg-white p-4 text-center tracking-[-0.6px] text-[#131313]">
        <h3 className="min-w-12 rounded-full px-2.5 py-1 text-xs font-semibold leading-[normal] text-white" style={{ backgroundColor: color }}>
          {card.label}
        </h3>
        <img data-testid={`aiq-info-${domain}-icon`} src={CUMULATIVE_INFO_ICONS[domain]} alt="" aria-hidden="true" className="size-12 shrink-0 object-contain" />
        <div className="flex w-full flex-col gap-2 leading-[1.5]">
          <p data-testid={`aiq-info-${domain}-headline`} className="whitespace-pre-line text-sm font-bold">{card.headline}</p>
          <div className="flex flex-col gap-1 text-xs">
            <p className="whitespace-pre-line text-[#333]">{card.body}</p>
            <p className="whitespace-pre-line font-semibold" style={{ color }}>{card.emphasis}</p>
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className="flex min-h-[245px] flex-col items-center gap-4 rounded-lg bg-white px-5 py-4 text-center tracking-[-0.6px] text-[#131313]">
      <h3 className="text-base font-bold leading-[1.2]">{TYPE_MAP[domain]}</h3>
      <img
        data-testid={`aiq-info-${domain}-icon`}
        src={AIQ_DOMAIN_ICONS[domain]}
        alt=""
        className="size-12 shrink-0"
        aria-hidden="true"
      />
      <div className="flex flex-col gap-1 pt-2 text-xs font-normal leading-[1.5]">
        <p
          data-testid={`aiq-info-${domain}-headline`}
          className="break-keep whitespace-pre-line font-semibold"
        >
          {copy?.infoCards?.[domain]?.headline ?? t(`studentAiqReport.aiqInfo.cards.${domain}.headline`)}
        </p>
        <p className={copy?.infoCards?.[domain] ? 'break-keep' : undefined}>
          {copy?.infoCards?.[domain]?.body ?? t(`studentAiqReport.aiqInfo.cards.${domain}.body`)}
        </p>
      </div>
    </div>
  )
}

export function MiniBellCurve({ score, rank }: { score: number; rank: number }) {
  const { t } = useTranslation('admin')

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Figma: score row line-height 1.2, curve group 98px (the badge may overhang into the card padding). */}
      <div className="flex items-center gap-1">
        <span className="text-2xl font-bold leading-[1.2] text-[#131313]">{score}</span>
        <span className="text-base font-medium leading-[1.2] text-[#888]">{t('studentAiqReport.distRank', { rank })}</span>
      </div>
      <div className="h-[98px] w-[242px]">
        <AiqBellCurve score={score} variant="compact" />
      </div>
    </div>
  )
}

function MiniCohortBars({
  nickName,
  aiqScore,
  sameAgeAiqScore,
  quebonAiqScore,
  sameGroupLabel,
  topGroupLabel,
}: {
  nickName: string
  aiqScore: number
  sameAgeAiqScore: number
  quebonAiqScore: number
  sameGroupLabel?: string
  topGroupLabel?: string
}) {
  const { t } = useTranslation('admin')
  const items = [
    { key: 'self', label: nickName, value: aiqScore, barColor: '#4599FF', labelColor: '#4599FF' },
    { key: 'sameTest', label: sameGroupLabel ?? t('studentAiqReport.sameTest'), value: sameAgeAiqScore, barColor: '#D0D0D0', labelColor: '#888888' },
    { key: 'quebon', label: topGroupLabel ?? t('studentAiqReport.quebonTopRank'), value: quebonAiqScore, barColor: '#D0D0D0', labelColor: '#888888' },
  ]
  return (
    <div data-testid="aiq-cohort-chart" className="relative h-[150px] w-full break-inside-avoid overflow-visible pb-2">
      <div data-testid="aiq-cohort-plot" className="relative h-[112px] border-b border-[#d0d0d0]">
        <div data-testid="aiq-cohort-grid" className="pointer-events-none absolute inset-0 z-0">
          {[0, 25, 50, 75].map((top) => (
            <div
              key={top}
              className="absolute inset-x-0 border-t border-dashed border-[#d0d0d0]"
              style={{ top: `${top}%` }}
            />
          ))}
        </div>
        <div data-testid="aiq-cohort-series" className="absolute inset-0 z-10 grid grid-cols-3 items-end px-7">
          {items.map((item) => (
            <div key={item.key} className="flex h-full min-w-0 flex-col items-center justify-end">
              <span
                data-testid={`aiq-cohort-value-${item.key}`}
                className={`mb-2 text-xs leading-[1.2] ${item.key === 'self' ? 'font-bold' : 'font-semibold'}`}
                style={{ color: item.labelColor }}
              >
                {item.value}
              </span>
              <div
                data-testid={`aiq-cohort-bar-${item.key}`}
                style={{
                  height: `${Math.max(0, Math.min(150, item.value)) / 150 * MINI_COHORT_BAR_MAX_HEIGHT}px`,
                  width: '24px',
                  backgroundColor: item.barColor,
                  borderTopLeftRadius: '2px',
                  borderTopRightRadius: '2px',
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <div data-testid="aiq-cohort-labels" className="grid grid-cols-3 px-7 pt-2">
        {items.map((item) => (
          <span
            key={item.key}
            data-testid={`aiq-cohort-label-${item.key}`}
            className="min-w-0 whitespace-pre-line text-center text-[10px] font-medium leading-[1.5]"
            style={{ color: item.labelColor }}
          >
            {item.label}
          </span>
        ))}
      </div>
    </div>
  )
}

export function DomainComparisonChart({
  domainScores,
  nickName,
  copy,
  compact = false,
}: {
  domainScores: AiqDomainScore[]
  nickName: string
  copy?: AiqReportCopy
  /** 카드 안에 넣을 때 아래 여백을 없애되, 범례와 100점 라벨 사이 간격은 확보한다. */
  compact?: boolean
}) {
  const { t } = useTranslation('admin')
  const orderedScores = DOMAIN_ORDER
    .map((domain) => domainScores.find((score) => score.domain === domain))
    .filter((score): score is AiqDomainScore => score !== undefined)

  return (
    <section data-testid="aiq-domain-bars" className={`flex flex-col items-end ${compact ? 'gap-5' : 'gap-6 pb-2'}`}>
      <div data-testid="aiq-domain-legend" className="flex items-center gap-4 text-xs font-medium leading-[14px] text-[#888]">
        <Legend color={DOMAIN_SERIES.self.bar} label={nickName} />
        <Legend color={DOMAIN_SERIES.sameAge.bar} label={copy?.sameGroupLabel ?? t('studentAiqReport.sameTest')} />
        <Legend color={DOMAIN_SERIES.quebon.bar} label={copy?.topGroupLabel ?? t('studentAiqReport.quebonLearner')} />
      </div>
      <div className="w-full">
        <div data-testid="aiq-domain-plot" className="relative h-[135px] border-b border-[#d0d0d0]">
          <div data-testid="aiq-domain-grid" className="pointer-events-none absolute inset-0 z-0">
            {[15, 48, 75, 105].map((top) => (
              <div key={top} className="absolute left-0 right-0 border-t border-dashed border-[#d0d0d0]" style={{ top }} />
            ))}
          </div>
          <div
            data-testid="aiq-domain-series"
            className="absolute inset-0 z-10 grid grid-cols-[repeat(3,92px)] grid-rows-[minmax(0,1fr)] items-end justify-center gap-x-[99px]"
          >
            {orderedScores.map((score) => <DomainBarGroup key={score.domain} score={score} />)}
          </div>
        </div>
        <div
          data-testid="aiq-domain-labels"
          className="grid grid-cols-[repeat(3,92px)] justify-center gap-x-[99px] pt-2"
        >
          {orderedScores.map((score) => (
            <span key={score.domain} className="text-center text-xs font-medium leading-[1.5] text-[#131313]">
              {TYPE_MAP[score.domain]}
            </span>
          ))}
        </div>
      </div>
      {copy?.comparisonFootnote ? <ComparisonFootnote>{copy.comparisonFootnote}</ComparisonFootnote> : null}
    </section>
  )
}

function ComparisonFootnote({ children }: { children: string }) {
  return <p className="w-full whitespace-pre-line text-[11px] leading-[1.5] text-[#888]">{children}</p>
}

function DomainBarGroup({ score }: { score: AiqDomainScore }) {
  return (
    <div
      data-testid={`aiq-domain-group-${score.domain}`}
      className="flex h-full w-[92px] items-end gap-2"
    >
      <DomainBar series="self" domain={score.domain} value={score.score ?? 0} colors={DOMAIN_SERIES.self} />
      <DomainBar series="sameAge" domain={score.domain} value={score.sameAgeScore} colors={DOMAIN_SERIES.sameAge} />
      <DomainBar series="quebon" domain={score.domain} value={score.quebonScore} colors={DOMAIN_SERIES.quebon} />
    </div>
  )
}

function DomainBar({
  series,
  domain,
  value,
  colors,
}: {
  series: 'self' | 'sameAge' | 'quebon'
  domain: AiqDomain
  value: number
  colors: { bar: string; text: string }
}) {
  const width = series === 'self' ? '28px' : '24px'

  return (
    <div className="flex flex-col items-center gap-[10px]" style={{ width }}>
      <span
        data-testid={`aiq-domain-value-${domain}-${series}`}
        className="w-full text-center text-xs font-bold leading-[1.2]"
        style={{ color: colors.text }}
      >
        {value}
      </span>
      <div
        data-testid={`aiq-domain-bar-${domain}-${series}`}
        style={{
          height: `${Math.max(0, Math.min(100, value)) / 100 * DOMAIN_BAR_MAX_HEIGHT}px`,
          width,
          backgroundColor: colors.bar,
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
        }}
      />
    </div>
  )
}

function DomainDetailCard({ score, copy }: { score: AiqDomainScore; copy?: AiqReportCopy }) {
  const { t } = useTranslation('admin')
  const value = score.score ?? 0
  const diff = value - score.sameAgeScore
  const diffColor = diff < 0 ? '#ff675e' : '#4599ff'
  const compareText = diff === 0 ? t('studentAiqReport.detail.same') : scoreDiffText(score.score, score.sameAgeScore)

  return (
    <article
      data-testid={`aiq-domain-detail-${score.domain}`}
      className="flex min-h-[158px] items-center gap-4 rounded-lg bg-[#f5f5f5] px-6 py-5 tracking-[-0.6px] text-[#131313]"
    >
      <div className="flex w-60 shrink-0 flex-col gap-2">
        <div className="font-bold">
          <p data-testid={`aiq-domain-ability-${score.domain}`} className="text-base leading-[1.5]">
            {t('studentAiqReport.detail.ability', { type: TYPE_MAP[score.domain] })}
          </p>
          <p data-testid={`aiq-domain-score-${score.domain}`} className="text-xl leading-[1.5]">
            <span style={{ color: diffColor }}>{value}점</span>
            {t('studentAiqReport.detail.scoreSuffix')}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          <p data-testid={`aiq-domain-comparison-${score.domain}`} className="text-sm font-semibold leading-[1.5]">
            {copy?.sameAveragePrefix ?? t('studentAiqReport.detail.sameAveragePrefix')}{' '}
            <span style={{ color: diffColor }}>{compareText}</span>
          </p>
          <p className="text-xs font-medium leading-[1.5] text-[#888]">
            {copy?.sameAverageValue?.(score.sameAgeScore)
              ?? t('studentAiqReport.detail.sameAverageValue', { value: score.sameAgeScore })}
          </p>
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2 text-xs leading-[1.5]">
        <p data-testid={`aiq-domain-slogan-${score.domain}`} className="font-bold">
          {copy?.domainHeadlines?.[score.domain] ?? DOMAIN_SLOGAN[score.domain]}
        </p>
        <p>{score.content || DOMAIN_DEFINITION[score.domain]}</p>
      </div>
    </article>
  )
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1">
      <span className="size-2.5 rounded-full" style={{ backgroundColor: color }} />
      <span>{label}</span>
    </div>
  )
}

export function PageNumber({ value }: { value: number }) {
  return (
    <div
      data-testid="report-page-number"
      className="absolute bottom-4 left-12 flex w-[698px] justify-center px-2 text-sm font-medium text-[#333]"
    >
      {value}
    </div>
  )
}

function bandTop(band: MathBand) {
  if (band === 'high') return 7
  if (band === 'mid') return 43
  return 79
}

function formatAiqDate(value: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value.slice(0, 10).replace(/-/g, '. ')
  const p = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}. ${p(date.getMonth() + 1)}. ${p(date.getDate())}`
}
